import { z, ZodTypeAny } from 'zod';
import { ModuleSchema } from '../shared-schemas.js';
import * as googleLibphonenumber from 'google-libphonenumber'; // eslint-disable-line import/no-namespace

import {
  AgentApplicantAuthorisationStatus,
  CourseCompare,
  DisabilityType,
  EmploymentType,
  EnglishTestNotReqdReason,
  EnglishTestType,
  Ethnicity,
  FeeStatus,
  FirstApplicationStatus,
  Gender,
  GenderIdentity,
  GradeLevel,
  // GradeSystem,
  IDType,
  PreviousStudyLevel,
  ReferralSource,
  RefusedVisaType,
  ResultStatus,
  StudyLevel,
  StudyMode,
  UkAccommodationType,
  VisaLevelOfStudy,
  VisaOrPermitType,
  VisaType,
} from '../enums.js';
import { EntryPoint } from '../offer/enums.js';
import { RouteStatus, SectionRoute, YourInformationTaskRoute } from '../route/index.js';

import { intakeIdValidator } from '../schemas/index.js';
import { PassportSchema } from '../shared/index.js';
import { IEllucianData } from '../application/index.js';
import { isValidPhoneCode } from '../../data/phoneCountryCodes.js';
import { IUserRole } from '../../user/index.js';
import { PotentialMatchStudent } from '../../integrations/data-processing/index.js';

const { PhoneNumberUtil } = googleLibphonenumber;

export * from './auth.js';
export * from './dashboard-row.js';
export * from './document-keys.js';

export const jsonParser = <T extends ZodTypeAny>(schema: T) =>
  z.preprocess((val, ctx) => {
    try {
      return typeof val === 'string' ? JSON.parse(val) : val;
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: e instanceof Error ? e.message : 'Invalid JSON format.',
      });
      return z.NEVER;
    }
  }, schema);

export const AttachDocumentSchema = z.object({
  sKey: z.string().nullable(), // Allow null for deletion, required for uploads
  s3Keys: z.array(z.string()).nullable(), // Allow null for deletion, required for uploads
});

export const ProgrammeChoiceSchema = z.object({
  intakeId: intakeIdValidator.min(1, 'Please select an intake'),
  intendedLevelOfStudy: z.nativeEnum(StudyLevel, { message: 'Please select a study level' }),
  courseName: z.string().min(1, { message: 'Please select a course name' }),
  mode: z.nativeEnum(StudyMode, { message: 'Please select a study mode' }),
  campus: z.string().min(1, { message: 'Please select a campus' }),
  entryPoint: z.nativeEnum(EntryPoint, { message: 'Please select an entry year' }),
  courseId: z.string().min(1, { message: 'Please select a course' }),
});

export const MAX_PROGRAMME_CHOICES = 6;

// Programme choices error messages
export const PROGRAMME_CHOICES_ERROR_MESSAGES = {
  required: {
    intake: 'Please select an intake to continue',
    level: 'Please select your intended level of study',
    programme: 'Please select a programme',
    mode: 'Please select a study mode',
    campus: 'Please select a campus',
    entryPoint: 'Please select an entry year',
    general: 'Please fill in all required fields',
  },
  duplicate: 'You have already selected this programme combination',
  minimum: 'You must select at least one programme to proceed',
  maximum: `You have reached the maximum of ${MAX_PROGRAMME_CHOICES} programme choices`,
  noProgrammes: 'No programmes are available for your selected criteria. Please try different options.',
  noCourses: 'No programmes are available for your selected criteria. Please try different options.',
  serverError: 'We encountered an error saving your choices. Please try again.',
  invalidData: 'Some of your selections are invalid. Please check and try again.',
};

// Helper function to check if a programme choice is a duplicate
export const isDuplicateProgramme = (
  existingChoices: IProgrammeChoice[],
  newChoice: Partial<IProgrammeChoice>,
  editingIndex?: number,
): boolean => {
  return existingChoices.some((existing, index) => {
    // Skip the item being edited
    if (editingIndex !== undefined && index === editingIndex) {
      return false;
    }

    // Check if the courseId already exists (courseId includes intakeId, shortId, mode, and entryPoint)
    return existing.courseId === newChoice.courseId;
  });
};

// Validate programme choices before submission
export const validateProgrammeChoices = (
  programmeChoices: IProgrammeChoice[],
  withMax: boolean = false,
): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  // Check minimum
  if ((programmeChoices?.length ?? 0) === 0) {
    errors.push(PROGRAMME_CHOICES_ERROR_MESSAGES.minimum);
  }

  // Check maximum
  if (withMax && (programmeChoices?.length ?? 0) > MAX_PROGRAMME_CHOICES) {
    errors.push(PROGRAMME_CHOICES_ERROR_MESSAGES.maximum);
  }

  // Check for duplicates
  const uniqueCombos = new Set<string>();
  for (const choice of programmeChoices) {
    // courseId already contains intakeId, shortId, mode, and entryPoint
    if (uniqueCombos.has(choice.courseId)) {
      errors.push(PROGRAMME_CHOICES_ERROR_MESSAGES.duplicate);
      break;
    }
    uniqueCombos.add(choice.courseId);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Helper to get field-specific error message
export const getProgrammeFieldError = (field: string, errorType: string = 'required'): string => {
  if (errorType === 'required') {
    const requiredErrors = PROGRAMME_CHOICES_ERROR_MESSAGES.required as Record<string, string>;
    return requiredErrors[field] || requiredErrors.general;
  }
  const message = PROGRAMME_CHOICES_ERROR_MESSAGES[errorType as keyof typeof PROGRAMME_CHOICES_ERROR_MESSAGES];
  return typeof message === 'string' ? message : 'Validation error';
};

const createProgrammeChoicesSchema = (includeMax: boolean) => {
  let schema = z.array(ProgrammeChoiceSchema).min(1, 'You must add at least one programme choice to proceed');

  if (includeMax) {
    schema = schema.max(MAX_PROGRAMME_CHOICES, 'You have added the maximum number of choices');
  }

  return z.object({
    programmeChoices: schema.refine(
      choices => {
        // Check for duplicate programme combinations (courseId already contains intakeId, shortId, mode, and entryPoint)
        const uniqueCombos = new Set();
        for (const choice of choices) {
          if (uniqueCombos.has(choice.courseId)) {
            return false;
          }
          uniqueCombos.add(choice.courseId);
        }
        return true;
      },
      {
        message: 'Duplicate programme combinations are not allowed',
      },
    ),
  });
};
export const ProgrammeChoicesSchema = createProgrammeChoicesSchema(false);
export const ProgrammeChoicesSchemaWithMax = createProgrammeChoicesSchema(true);

export const AddressSchema = z.object({
  addressLine1: z.string().min(1, "Address Line 1 can't be empty").max(50, 'Address Line 1 is too long'),
  addressLine2: z.string().max(50, 'Address line 2 is too long').optional(),
  addressLine3: z.string().max(50, 'Address line 3 is too long').optional(),
  city: z.string().min(1, "City can't be empty").max(50, 'City is too long'),
  addressHasSubdivision: z.boolean().default(false),
  subdivisionCode: z.string().max(10, 'Subdivision code is too long').default(''),
  state: z.string().max(50, 'State is too long').optional(),
  postcode: z.string().min(1, "Postcode can't be empty.").max(20, 'Postcode is too long'),
  country: z.string(),
});

export const employmentAddressSchema = AddressSchema.omit({
  addressLine1: true,
  addressLine2: true,
  addressLine3: true,
  postcode: true,
}).extend({
  // These specific address fields have different names and this is to be maintained for backwards compatibility
  employerAddress: z.string().min(1, 'Address line 1 cannot be empty').max(50, 'Address line 1 is too long'),
  employerAddressLine2: z.string().max(50, 'Address line 2 is too long').optional(),
  employerAddressLine3: z.string().max(50, 'Address line 3 is too long').optional(),
  postalCode: z.string().min(1, "Postal code can't be empty.").max(20, 'Postal code is too long'),
});

export const FeeStatusSchema = z.object({
  feeStatus: z.nativeEnum(FeeStatus),
});

export const VisaRequiredSchema = z.object({
  isVisaRequired: z.boolean(),
});

export const HighestQualificationSchema = z.object({
  highestQualification: z.string(),
});

// Reusable phone number validation schema
export const PhoneNumberSchema = z
  .string()
  .min(1, 'Phone number is required.')
  .min(2, 'Invalid phone number length.')
  .regex(/^\d+(-\d+)*$/, 'Invalid phone number.')
  .refine((value: string) => value.includes('-'), { message: 'Phone number must include country code.' })
  .refine(
    (value: string) => {
      const [code, phone] = value.split('-');
      return code && phone && code.length && phone.length && /^\d+$/.test(code) && /^\d+$/.test(phone);
    },
    { message: 'Invalid phone number.' },
  )
  .refine(
    (value: string) => {
      const [code] = value.split('-');
      // Check if the country code is valid by looking it up in our phone country codes
      return isValidPhoneCode(code);
    },
    { message: 'Invalid country code.' },
  )
  .refine(
    (value: string) => {
      const [code, phone] = value.split('-');

      // Use google-libphonenumber for proper validation
      try {
        const util = PhoneNumberUtil.getInstance();

        // Create phone number object
        const rawNumber = `${code}${phone}`.replace(/[^0-9]/g, '');
        const phoneNum = util.parseAndKeepRawInput(`+${rawNumber}`);

        // Check if the number is possible (correct length for the country)
        const isPossible = util.isPossibleNumber(phoneNum);
        if (!isPossible) {
          return false;
        }

        // Check if the number is valid (passes all validation rules)
        const isValid = util.isValidNumber(phoneNum);

        // Special case for Caribbean format numbers (4-digit country code + 7-digit number)
        const isCaribbeanValid = /^[0-9]{4}$/.test(code) && /^[0-9]{7}$/.test(phone);

        return isValid || isCaribbeanValid;
      } catch (error) {
        // Fallback to basic validation if google-libphonenumber fails
        const minDigits = 7;
        const maxDigits = 15;
        return code.length >= 1 && code.length <= 4 && phone.length >= minDigits && phone.length <= maxDigits;
      }
    },
    { message: 'Your phone number is invalid or incomplete.' },
  );

export const ContactInformationSchema = z.object({
  nationality: z.string(),
  dob: z
    .string()
    .date()
    .refine(
      val => {
        const [YYYY, MM, DD] = val.split('-');
        return (
          DD &&
          MM &&
          YYYY &&
          DD.length === 2 &&
          MM.length === 2 &&
          YYYY.length === 4 &&
          parseInt(DD) <= 31 &&
          parseInt(MM) <= 12 &&
          parseInt(YYYY) > 1900
        );
      },
      { message: 'Date of birth is incomplete or invalid.' },
    )
    .refine(
      val => {
        const now = new Date();
        const dob = new Date(val);
        // not equal dateStrings allows to return FALSE if dob and now are the same date
        // allow only dates before now
        return dob < now && dob.toDateString() !== now.toDateString();
      },
      { message: 'Date of birth cannot be in the future or today' },
    )
    .refine(
      val => {
        const [year, month, day] = val.split('-').map(Number);
        const today = new Date();
        const todayMonth = today.getMonth() + 1; // getMonth() is zero based: Jan = 0
        const age =
          today.getFullYear() - year - (todayMonth < month || (todayMonth === month && today.getDate() < day) ? 1 : 0);
        return age > 13;
      },
      { message: 'Must be older than 13 years old.' },
    ),
  phoneNumber: PhoneNumberSchema,
});

/**
 * Complete qualification schema used across the application.
 *
 * Used by:
 * - Education History page (Applications flow)
 * - Agent/Staff APIs for managing qualifications
 * - Offer Requirements (Academic Results)
 *
 * For Highest Study flow, use HighestStudyAddQualificationSchema instead,
 * which excludes fields collected in earlier steps (level, gradeSystem).
 *
 * Note: level and levelLabel are now optional. In YHS, we no longer collect level
 * as it's not part of the new modal-based flow. These fields are kept for backward
 * compatibility with existing data and Education History flow.
 */
export const QualificationSchema = z
  .object({
    level: z
      .string()
      .refine(val => val in GradeLevel)
      .optional(),
    levelLabel: z.string().optional(),
    qualificationTypeId: z.string().optional(), // For YHS: qualification type ID from database (e.g., "34", "125")
    qualificationTypeLabel: z.string().optional(), // For YHS: qualification type label (e.g., "Advanced Extension Award")
    otherQualificationName: z.string().nullish(),
    subject: z.string().optional(),
    gradeSystem: z.string().min(1, 'Please select the grading system'),
    gradeSystemLabel: z.string().optional(),
    groupId: z.string().optional(), // For YHS: tracks which study group this qualification belongs to
    qualificationTypeRowId: z.string().optional(), // For YHS: tracks which specific studyRow (qualification type row) within the group this qualification belongs to
    grade: z.string().min(1, "Grade can't be empty").max(100, 'Grade is too long.'),
    graduationDate: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val) return true; // Allow undefined/empty when optional
          const currentYear = new Date().getFullYear();
          const MIN_YEAR = currentYear - 130;
          const MAX_YEAR = currentYear + 50;
          const [year] = val.split('-');
          const graduationDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
          return graduationDateYear && graduationDateYear > MIN_YEAR && graduationDateYear < MAX_YEAR;
        },
        { message: 'Invalid date' },
      ),
    awardingOrganisationId: z.string().optional(),
    awardingOrganisationLabel: z.string().optional(),
    // Visibility flags from qualification type metadata (stored for future reference)
    awardingOrganisationVisible: z.boolean().optional(),
    modulesVisible: z.boolean().optional(),
    subjectsVisible: z.boolean().optional(),
    modules: z.array(ModuleSchema).optional(),
  })
  .superRefine((values, ctx) => {
    const { level, otherQualificationName } = values;
    // Only validate otherQualificationName if level is provided and is "Other"
    if (level) {
      const isLevelOther = GradeLevel[level as keyof typeof GradeLevel] === GradeLevel.Other;
      // We no longer collect a separate "otherQualificationName"; do not require it.
      if (isLevelOther && typeof otherQualificationName === 'string' && otherQualificationName.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Qualification name is required',
          path: ['otherQualificationName'],
        });
      }
    }
  });

/**
 *
 * Adds validation to QualificationSchema with `StudySchema.date` and `StudySchema.validUntil`
 *
 * @param date
 * @param validUntil
 * @returns z.ZodEffects<typeof QualificationSchema>
 */
export const enhanceQualificationSchema = (
  date?: string,
  _country?: string,
): z.ZodEffects<typeof QualificationSchema> => {
  return QualificationSchema.superRefine((data, ctx) => {
    // TODO: review this later
    // Skip cross-check when study start date is not available yet
    if (!date || !data.graduationDate) return;
    const startDate = new Date(date);
    const targetDate = new Date(data.graduationDate);
    const isDateValid = targetDate >= startDate;
    if (!isDateValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Qualification date must be after ${date}`,
        path: ['graduationDate'],
      });
    }
  });
};

// Step 3 (Highest Study) form schema: exclude 'level' because it's collected earlier
export const QualificationStep3Schema = z.object({
  otherQualificationName: z.string().nullish(),
  subject: z.string().min(1, "Subject can't be empty").max(100, 'Subject is too long.'),
  gradeSystem: z.string().optional(),
  gradeSystemLabel: z.string().optional(),
  grade: z.string().min(1, "Grade can't be empty").max(100, 'Grade is too long.'),
  graduationDate: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val) return true; // Allow undefined/empty when optional
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year] = val.split('-');
        const graduationDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return graduationDateYear && graduationDateYear > MIN_YEAR && graduationDateYear < MAX_YEAR;
      },
      { message: 'Invalid date' },
    ),
  awardingOrganisationId: z.string().min(1, 'Please select an awarding organisation'),
  awardingOrganisationLabel: z.string().optional(),
  modules: z.array(ModuleSchema),
});

export const enhanceQualificationStep3Schema = (
  date?: string,
  _country?: string,
): z.ZodEffects<typeof QualificationStep3Schema> => {
  return QualificationStep3Schema.superRefine(
    (data: z.infer<typeof QualificationStep3Schema>, ctx: z.RefinementCtx) => {
      if (!date || !data.graduationDate) return; // If we don't have the study start date or graduation date, skip cross-field validation
      const startDate = new Date(date);
      const targetDate = new Date(data.graduationDate as string);
      const isDateValid = targetDate >= startDate;
      if (!isDateValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Qualification date must be after your study start date',
          path: ['graduationDate'],
        });
      }
    },
  );
};

export type IQualificationStep3 = z.infer<typeof QualificationStep3Schema>;

export const StudySchema = z
  .object({
    level: z.nativeEnum(PreviousStudyLevel).optional(),
    resultStatus: z.nativeEnum(ResultStatus),
    country: z.string().min(1, `Country can't be empty`),
    institutionName: z.string().min(1, "The name can't be empty"),
    institutionAddress: jsonParser(AddressSchema.omit({ country: true })),
    taughtInEnglish: z.boolean().optional(),
    // Persist Step 2 multi-type selections for Highest Study revisit/prefill
    qualificationTypeSelections: z
      .array(
        z.object({
          id: z.string().optional(), // unique identifier for this studyRow (to distinguish multiple rows with same qualificationType)
          qualificationType: z.string(),
          qualificationTypeLabel: z.string().optional(),
          customQualificationTypeName: z.string().optional(),
          gradingSystem: z.string(),
          gradingSystemLabel: z.string().optional(),
          groupId: z.string().optional(),
          resultStatus: z.nativeEnum(ResultStatus).optional(), // Per-group result status
          taughtInEnglish: z.boolean().optional(), // Per-group taught in English
        }),
      )
      .optional(),
    // Highest Study page does not collect study start/end dates yet.
    // Make them optional and validate only when provided.
    date: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val) return true;
          const currentYear = new Date().getFullYear();
          const MIN_YEAR = currentYear - 130;
          const MAX_YEAR = currentYear + 50;
          const [year, mm] = val.split('-');
          const isValidMonth = mm && mm.length === 2;
          const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
          return !!validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidMonth;
        },
        { message: 'Invalid date' },
      ),
    validUntil: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val) return true;
          const currentYear = new Date().getFullYear();
          const MIN_YEAR = currentYear - 130;
          const MAX_YEAR = currentYear + 50;
          const [year, mm] = val.split('-');
          const isValidMonth = mm && mm.length === 2;
          const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
          return !!validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidMonth;
        },
        { message: 'Invalid date' },
      ),
    academicTranscriptDocument: AttachDocumentSchema.optional(),
    qualifications: z.record(z.string(), QualificationSchema).optional(),
  })
  .superRefine((values, ctx) => {
    if (values.date) {
      const [startDateYear, startDateMonth] = values.date.split('-');
      if (startDateYear && startDateMonth && startDateYear.length === 4 && startDateMonth.length === 2) {
        const startDateObj = new Date(`${startDateYear}-${startDateMonth}-01`);
        const today = new Date();
        // Set today to the first of the current month for fair comparison
        const todayFirstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        if (startDateObj > todayFirstOfMonth) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['date'],
            message: 'Start date cannot be in the future',
          });
        }
      }
    }

    if (values.date && values.validUntil) {
      if (new Date(values.date) >= new Date(values.validUntil)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['date'],
          message: 'Start date could not be later then the end date',
        });
      }
    }

    const stringHasContent = (str: string | undefined): boolean => {
      return str !== undefined && str.trim().length > 0;
    };

    if (values.institutionAddress.addressHasSubdivision) {
      if (
        !stringHasContent(values.institutionAddress.state) ||
        !stringHasContent(values.institutionAddress.subdivisionCode)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['institutionAddress', 'state'],
          message: 'Please select a valid state/province for the selected country',
        });
      }
    }
  });

export const AddUpdateStudyHistorySchema = z.object({
  studyHisitoryId: z.string().optional(),
  action: z.enum(['ADD', 'UPDATE', 'REMOVE']),
  studyHistory: StudySchema.optional(),
});

export const StudyHistorySchema = z.object({
  highest: StudySchema.optional(),
  previous: z.record(z.string(), StudySchema).optional(),
});

export const ApplicantReferralSchema = z
  .object({
    firstApplicationStatus: z.nativeEnum(FirstApplicationStatus),
    ucasId: z.string().optional(),
    referralSource: z.nativeEnum(ReferralSource),
    studentReferrerName: z.string().optional(),
    previousApplicationOrStudentNumber: z.string().optional(),
    previousStudentNumber: z.string().optional(),
  })
  .refine(
    val => {
      if (val.firstApplicationStatus === FirstApplicationStatus.FirstTime) {
        return val.referralSource !== undefined;
      }
      return true;
    },
    {
      message: 'Referral source is required for first-time applications',
      path: ['referralSource'],
    },
  )
  .refine(
    val => {
      if (val.firstApplicationStatus === FirstApplicationStatus.AppliedOnly) {
        return (
          val.previousApplicationOrStudentNumber !== undefined && val.previousApplicationOrStudentNumber.trim() !== ''
        );
      }
      return true;
    },
    {
      message: 'Previous application number is required when you have applied before',
      path: ['previousApplicationOrStudentNumber'],
    },
  )
  .refine(
    val => {
      if (val.firstApplicationStatus === FirstApplicationStatus.StudiedBefore) {
        return val.previousStudentNumber !== undefined && val.previousStudentNumber.trim() !== '';
      }
      return true;
    },
    {
      message: 'Previous student number is required when you have enrolled before',
      path: ['previousStudentNumber'],
    },
  );

export const OtherNameSchema = z.object({
  givenName: z.string().min(1),
  middleName: z.string().optional(),
  familyName: z.string().min(1),
  reason: z.string().min(1),
});

export const ApplicantIdentitySchema = z.object({
  givenName: z
    .string()
    .regex(
      /^[a-zA-Z\s'-]+$/,
      `Use letters A–Z only, plus spaces, hyphens (-) or apostrophes ('). Accents and special characters aren’t supported`,
    )
    .min(1, 'Given name is required')
    .max(128, 'Given name is too long'),
  middleName: z
    .string()
    .regex(
      /^[a-zA-Z\s'-]*$/,
      `Use letters A–Z only, plus spaces, hyphens (-) or apostrophes ('). Accents and special characters aren’t supported`,
    )
    .max(128, 'Middle name is too long')
    .nullish()
    .optional(),
  familyName: z
    .string()
    .regex(
      /^[a-zA-Z\s'-]+$/,
      `Use letters A–Z only, plus spaces, hyphens (-) or apostrophes ('). Accents and special characters aren’t supported`,
    )
    .max(128, 'Family name is too long')
    .min(1, 'Family name is required'),
  title: z.string().min(1),
  gender: z.nativeEnum(Gender),
  genderIdentity: z.nativeEnum(GenderIdentity).optional(),

  passport: PassportSchema.nullish(),
  idType: z.nativeEnum(IDType),
  otherNames: z.record(z.string(), OtherNameSchema).nullish(),
  passportDocument: AttachDocumentSchema.optional(),
  idDocument: AttachDocumentSchema.optional(),
});

// TODO: remove this schema when start with this ticket ENROLY-1320
export const ApplicantIdentityValidationSchema = ApplicantIdentitySchema.extend({
  hasOtherName: z.boolean(),
  otherNames: z.array(OtherNameSchema).nullish(),
}).refine(
  values => {
    const { hasOtherName, otherNames } = values;
    if (hasOtherName) {
      return (otherNames ?? []).length !== 0;
    }
    return true;
  },
  { message: 'Add other name', path: ['otherNames'] },
);

// Identity schema with file validation for passport/ID documents
// Updated: Make file uploads optional for Identity. We keep the schema wrapper
// in place but do not add issues when files are missing.
export const ApplicantIdentityValidationWithFiles = (
  _passportFiles?: string[],
  _idFiles?: string[],
  genderEnabled: boolean = false,
): z.ZodEffects<typeof ApplicantIdentityValidationSchema> => {
  return ApplicantIdentityValidationSchema.superRefine((data, ctx) => {
    // Conditional Gender Identity Check
    if (genderEnabled && !data.genderIdentity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['genderIdentity'],
      });
    }
  });
  // Files are optional; do not enforce file presence for passport or photo ID selections.
};

export const OtherNamePayloadSchema = z.object({
  otherName: OtherNameSchema,
});

export const OtherNamesSchema = z.object({
  otherNames: z.record(z.string(), OtherNameSchema).nullish(),
});

export const DisiblityTypeSchema = z.nativeEnum(DisabilityType);

export const DiversitySchema = z.object({
  ethnicity: z.nativeEnum(Ethnicity),
  hasDisability: z.boolean(),
  disabilityTypes: z.array(DisiblityTypeSchema).nullish().default(null),
  hasBeenInCare: z.boolean().nullish().default(null),
  careStartDate: z
    .string()
    .nullish()
    .default(null)
    .refine(
      value => {
        if (value === null) return true;
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = (value ?? '').split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' },
    ),
  careEndDate: z
    .string()
    .nullish()
    .default(null)
    .refine(
      value => {
        if (value === null) return true;
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = (value ?? '').split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' },
    ),
  careDetails: z.string().nullish().default(null),
  occupation: z.string().min(1, 'Cannot be empty').nullish().default(null),
  disabilityTypeOther: z.string().nullish().default(null),
  lastSchoolDescription: z.string().nullish().default(null),
  hasParentsInHE: z.boolean().nullish().default(null),
  isDisabilityStudentAllowance: z.boolean().nullish().default(null),
});

export const getDiversityValidationSchema = (isHomeStudent: boolean = false) => {
  const currentDate = new Date();

  return DiversitySchema.refine(
    values => {
      const { hasBeenInCare, careStartDate, careEndDate } = values;

      if (hasBeenInCare) {
        if (!careStartDate || !careEndDate) {
          return true;
        }

        const startDate = new Date(careStartDate);
        const endDate = new Date(careEndDate);

        return startDate < currentDate && endDate < currentDate && startDate <= endDate;
      }

      return true;
    },
    {
      message:
        'Invalid care dates: start date and end date must be in the past, and start date should not be after end date.',
      path: ['careStartDate'],
    },
  ).superRefine((values, ctx) => {
    const {
      ethnicity,
      hasDisability,
      disabilityTypes = [],
      disabilityTypeOther,
      hasBeenInCare,
      careStartDate,
      careEndDate,
      careDetails,
    } = values;

    const isEthnicityValid = !!ethnicity && typeof hasDisability === 'boolean';

    if (!isEthnicityValid)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ethnicity'], message: 'Please select an ethnicity' });

    const isDisabilityTypeValid = !hasDisability || (hasDisability && disabilityTypes && disabilityTypes.length > 0);
    if (!isDisabilityTypeValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disabilityTypes'],
        message: 'Required',
      });
    }

    const isHealthConditionValid =
      !hasDisability ||
      (disabilityTypes &&
        disabilityTypes.length &&
        (!disabilityTypes.includes(DisabilityType.NotListed) || !!disabilityTypeOther));
    if (!isHealthConditionValid)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disabilityTypeOther'],
        message: 'Required',
      });

    const isInCareValid =
      typeof hasBeenInCare !== 'boolean' || !hasBeenInCare || (!!careStartDate && !!careEndDate && !!careDetails);
    if (!isInCareValid) {
      for (const field of ['careStartDate', 'careEndDate', 'careDetails']) {
        if (values[field as keyof typeof values]) continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Required',
        });
      }
    }

    const homeStudentFields = [
      'isDisabilityStudentAllowance',
      'hasBeenInCare',
      'lastSchoolDescription',
      'hasParentsInHE',
      'occupation',
    ];

    const everyHomeFieldNotEmpty = homeStudentFields.every(f => {
      return values[f as keyof typeof values] !== null && values[f as keyof typeof values] !== undefined;
    });

    const isHomeStudentValid = !isHomeStudent || everyHomeFieldNotEmpty;

    if (!isHomeStudentValid) {
      for (const field of homeStudentFields) {
        if (values[field as keyof typeof values] !== null && values[field as keyof typeof values] !== undefined)
          continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Required',
        });
      }
    }

    if (
      !isEthnicityValid ||
      !isDisabilityTypeValid ||
      !isHealthConditionValid ||
      !isInCareValid ||
      !isHomeStudentValid
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The form is not completed, please fill in all required fields',
      });
    }
  });
};

export const OtherNationalitySchema = z.object({
  isCurrent: z.boolean(),
  nationality: z.string(),
});

export const ResidenceEntrySchema = z
  .object({
    countryOfResidence: z.string(),
    description: z.string().nullable(),
    areaOfResidence: z.string().nullable(),
    entryIntoUKDate: z.string().date().nullable(),
  })
  // check if description exists when UK is selected
  .refine(
    values => {
      if (values.countryOfResidence === 'United Kingdom') {
        return !!values.description;
      }
      return true;
    },
    {
      message: 'Description is required for UK residence.',
      path: ['description'],
    },
  )
  // check if entryIntoUKDate exists when UK is selected
  .refine(
    values => {
      if (values.countryOfResidence === 'United Kingdom') {
        return !!values.entryIntoUKDate;
      }
      return true;
    },
    {
      message: 'Date of entry into the UK is required.',
      path: ['entryIntoUKDate'],
    },
  )
  .refine(
    values => {
      if (values.countryOfResidence !== 'United Kingdom') {
        return true;
      }

      const { entryIntoUKDate } = values;
      const [YYYY, MM, DD] = typeof entryIntoUKDate === 'string' ? entryIntoUKDate.split('-') : '';
      return (
        DD &&
        MM &&
        YYYY &&
        DD.length === 2 &&
        MM.length === 2 &&
        YYYY.length === 4 &&
        parseInt(DD) <= 31 &&
        parseInt(MM) <= 12 &&
        parseInt(YYYY) > 1900
      );
    },
    {
      message: 'Date of entry into the UK is incomplete or invalid.',
      path: ['entryIntoUKDate'],
    },
  )
  .refine(
    values => {
      if (values.countryOfResidence !== 'United Kingdom') {
        return true;
      }

      const { entryIntoUKDate } = values;
      const now = new Date();
      const dob = new Date(typeof entryIntoUKDate === 'string' ? entryIntoUKDate : '');
      // not equal dateStrings allows to return FALSE if dob and now are the same date
      // allow only dates before now
      return dob < now && dob.toDateString() !== now.toDateString();
    },
    {
      message: 'Date of entry into the UK cannot be in the future or today',
      path: ['entryIntoUKDate'],
    },
  )
  // check if areaOfResidence exists when UK is selected
  .refine(
    values => {
      if (values.countryOfResidence === 'United Kingdom') {
        return !!values.areaOfResidence;
      }
      return true;
    },
    {
      message: 'Area of residence is required for UK residence.',
      path: ['areaOfResidence'],
    },
  );

export const OtherResidenceEntrySchema = z
  .object({
    countryOfResidence: z.string(),
    yearOfResidence: z.number(),
  })
  .refine(
    values => {
      const currentYear = new Date().getFullYear();
      return values.yearOfResidence <= currentYear;
    },
    {
      message: 'Year of residence cannot be in the future',
      path: ['yearOfResidence'],
    },
  )
  .refine(
    values => {
      return values.yearOfResidence >= 1900 && values.yearOfResidence.toString().length === 4;
    },
    {
      message: 'Please enter a valid 4-digit year (1900 or later)',
      path: ['yearOfResidence'],
    },
  );

export const NationalitySchema = z
  .object({
    applyingFrom: z.string(),
    countryOfBirth: z.string(),
    nationalityForApplicationPurposes: z.string(),
    isOnlyCountry: z.boolean(),
    isOtherResidence: z.boolean(),
    otherNationalities: z.array(OtherNationalitySchema).nullish(),
    primaryResidence: ResidenceEntrySchema,
    otherResidences: z.array(OtherResidenceEntrySchema).nullish(),
    statementsCorrect: z.boolean(),
  })
  .refine(
    values => {
      const { isOnlyCountry, otherNationalities } = values;
      if (isOnlyCountry) return otherNationalities && otherNationalities.length > 0;
      return true;
    },
    { message: 'Other nationalities cannot be left empty', path: ['isOnlyCountry'] },
  )
  .refine(
    values => {
      const { isOtherResidence, otherResidences } = values;
      if (isOtherResidence) return otherResidences && otherResidences.length > 0;
      return true;
    },
    { message: 'Other residences cannot be left empty', path: ['isOtherResidence'] },
  );

export const EmploymentHistorySchema = employmentAddressSchema
  .extend({
    jobTitle: z.string().min(1),
    employerName: z.string().min(1),
    type: z.nativeEnum(EmploymentType),
    startDate: z.string().refine(
      value => {
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = value.split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' },
    ),
    endDate: z
      .string()
      .refine(
        value => {
          if (value === undefined || value === '') return true;
          const [year] = value.split('-');
          if (year === undefined || year === '') return true;
          const currentYear = new Date().getFullYear();
          const MIN_YEAR = currentYear - 130;
          const MAX_YEAR = currentYear + 50;

          const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
          return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR;
        },
        { message: 'Invalid year' },
      )
      .optional(),
  })
  .superRefine((values, ctx) => {
    if (values.endDate) {
      if (new Date(values.startDate) >= new Date(values.endDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endDate'],
          message: 'End Date could not be earlier than the Start Date',
        });
      }
    }

    const stringHasContent = (str: string | undefined): boolean => {
      return str !== undefined && str.trim().length > 0;
    };

    if (values.addressHasSubdivision) {
      if (!stringHasContent(values.subdivisionCode) || !stringHasContent(values.state)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['state'],
          message: 'Please select a valid state/province for the selected country',
        });
      }
    }
  })
  .superRefine((values, ctx) => {
    const { country, state, addressHasSubdivision, subdivisionCode } = values;

    const stringHasContent = (str: string | undefined): boolean => {
      return !!(str && typeof str === 'string' && str.trim().length > 0);
    };

    if (addressHasSubdivision && (!stringHasContent(subdivisionCode) || !stringHasContent(state))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['state'],
        message: 'State/Province is required for the selected country',
      });
    }

    if (country === 'United Kingdom' && !stringHasContent(state)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['state'],
        message: 'UK addresses require a county',
      });
    }
  });

export const ApplicantEmploymentSchema = z.object({
  hasEmploymentHistory: z.boolean(),
  hasCV: z.boolean().optional(),
  employmentHistory: z.array(EmploymentHistorySchema).nullish(),
  cvDocument: AttachDocumentSchema.optional(),
});

// Base schemas for discriminated union
const EnglishTestTakenBaseSchema = z.object({
  hasTakenTest: z.literal(true),
  type: z.nativeEnum(EnglishTestType),
  typeOther: z.string().optional(),
  referenceNo: z.string(),
  score: z.string(),
  date: z
    .string()
    .min(1, 'English test date is required')
    .refine(
      value => {
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear;
        const [year, date] = value.split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear <= MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' },
    ),
  validUntil: z
    .string()
    .optional()
    .refine(
      value => {
        if (!value) return true; // Optional field
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = value.split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear <= MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' },
    ),
});

const EnglishTestNotTakenBaseSchema = z.object({
  hasTakenTest: z.literal(false),
  reasonNotReqd: z.nativeEnum(EnglishTestNotReqdReason),
  reasonNotReqdOther: z.string().optional(),
});

// Discriminated union for englishTest (base union without superRefine for type extraction)
const EnglishTestBaseUnion = z.discriminatedUnion('hasTakenTest', [
  EnglishTestTakenBaseSchema,
  EnglishTestNotTakenBaseSchema,
]);

// Add validation with superRefine for runtime validation
export const EnglishTestSchema = EnglishTestBaseUnion.superRefine((data, ctx) => {
  if (data.hasTakenTest) {
    const { type, typeOther, score, referenceNo, date, validUntil } = data;

    // score validation
    if (!score || score.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['score'],
        message: 'Test score is required',
      });
    }

    // Reference number validation
    if (!referenceNo || referenceNo.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['referenceNo'],
        message: 'Reference number is required',
      });
    }

    // OTHER type test name validation
    if (type === EnglishTestType.Other && (!typeOther || typeOther.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['typeOther'],
        message: 'Please specify the name of the English test',
      });
    }

    // Test date cannot be in the future
    if (date) {
      const [year, month] = date.split('-');
      if (year && month) {
        const testDate = new Date(date);
        const today = new Date();

        if (testDate > today) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['date'],
            message: 'The English Test Date cannot be later than today',
          });
        }
      }
    }

    // Valid until must be after test date
    if (date && validUntil) {
      const [testYear, testMonth] = date.split('-');
      const [validYear, validMonth] = validUntil.split('-');

      if (testYear && testMonth && validYear && validMonth) {
        // Create dates with first day of month for consistent comparison
        const testDate = new Date(`${testYear}-${testMonth}-01`);
        const validUntilDate = new Date(`${validYear}-${validMonth}-01`);

        if (testDate > validUntilDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validUntil'],
            message: 'The Valid Until date cannot be earlier than the Test Date.',
          });
        }
      }
    }
  } else if (data.hasTakenTest === false) {
    // Validation for when test was not taken
    const { reasonNotReqd, reasonNotReqdOther } = data;

    // If reason is "other" but no explanation provided
    if (reasonNotReqd === EnglishTestNotReqdReason.Other && (!reasonNotReqdOther || reasonNotReqdOther.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please provide an explanation',
        path: ['reasonNotReqdOther'],
      });
    }
  }
});

// Export typed versions for backward compatibility
export const EnglishTestTakenSchema = EnglishTestTakenBaseSchema;
export const EnglishTestNotTakenSchema = EnglishTestNotTakenBaseSchema;

// Type alias for the discriminated union (for type extraction, must be exported for interface)
export type EnglishTestUnion = z.infer<typeof EnglishTestBaseUnion>;

export const EnglishSchema = z
  .object({
    hasEnglishTest: z.boolean(),
    englishTest: EnglishTestSchema,
    reasonNotReqd: z.nativeEnum(EnglishTestNotReqdReason).optional().nullish(),
    reasonNotReqdOther: z.string().optional().nullish(),
    englishQualification: z.string().optional().nullish(),
    englishTestDocument: AttachDocumentSchema.optional(),
  })
  .superRefine((data, ctx) => {
    // Validate that englishTest.hasTakenTest matches hasEnglishTest
    if (data.englishTest.hasTakenTest !== data.hasEnglishTest) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'English test status mismatch',
        path: ['englishTest'],
      });
    }

    // Additional validation for backward compatibility fields
    if (!data.hasEnglishTest) {
      if (!data.reasonNotReqd) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a reason why an English test is not required',
          path: ['reasonNotReqd'],
        });
      }

      // If reason is "previous qualification" but no qualification selected
      if (data.reasonNotReqd === EnglishTestNotReqdReason.PrevQual && !data.englishQualification) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a qualification',
          path: ['englishQualification'],
        });
      }

      // If reason is "other" but no explanation provided
      if (
        data.reasonNotReqd === EnglishTestNotReqdReason.Other &&
        (!data.reasonNotReqdOther || data.reasonNotReqdOther.trim() === '')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please provide an explanation',
          path: ['reasonNotReqdOther'],
        });
      }
    }
  });

export const ContactDetailsSchema = z
  .object({
    residentialAddress: jsonParser(AddressSchema).nullish(),
    residentialPhoneNo: PhoneNumberSchema,
    contactAddressSameAsResidential: z.boolean(),
    contactAddress: jsonParser(AddressSchema.nullish()),
    termAddressSameAsPermanent: z.boolean().nullish().optional(),
    termAddress: jsonParser(AddressSchema.omit({ country: true }).nullish()),
    hasUkAccommodation: z.boolean().nullish().optional(),
    ukAccommodationType: z
      .string()
      .refine(val => val in UkAccommodationType)
      .nullish(),
    ukAccommodationAddress: jsonParser(AddressSchema.omit({ country: true }).nullish()),
    ukAccommodationPhone: PhoneNumberSchema.nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.residentialAddress?.addressHasSubdivision) {
      if (!data.residentialAddress.subdivisionCode || !data.residentialAddress.state) {
        ctx.addIssue({
          code: 'custom',
          path: ['residentialAddress', 'state'],
          message: 'State/Province is required for the selected country',
        });
      }
    }

    const stringHasContent = (str: string | undefined): boolean => {
      return str !== undefined && str.trim().length > 0;
    };

    if (data.contactAddress?.addressHasSubdivision) {
      if (!stringHasContent(data.contactAddress.subdivisionCode) || !stringHasContent(data.contactAddress.state)) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactAddress', 'state'],
          message: 'State/Province is required for the selected country',
        });
      }
    }

    if (data.termAddress?.addressHasSubdivision) {
      if (!stringHasContent(data.termAddress.subdivisionCode) || !stringHasContent(data.termAddress.state)) {
        ctx.addIssue({
          code: 'custom',
          path: ['termAddress', 'state'],
          message: 'State/Province is required for the selected country',
        });
      }
    }

    if (data.ukAccommodationAddress?.addressHasSubdivision) {
      ctx.addIssue({
        code: 'custom',
        path: ['ukAccommodationAddress', 'state'],
        message: 'UK accomodation does not require a state/province subdivision',
      });
    }

    if (data.contactAddressSameAsResidential === false && !data.contactAddress) {
      ctx.addIssue({
        code: 'custom',
        path: ['contactAddress'],
        message: 'Contact address is required when contact address is different',
      });
    }

    if (data.termAddressSameAsPermanent === false && !data.termAddress) {
      ctx.addIssue({
        code: 'custom',
        path: ['termAddress'],
        message: 'Term address is required when term address is different then permanent address',
      });
    }

    if (data.termAddressSameAsPermanent === false && !data.ukAccommodationType) {
      ctx.addIssue({
        code: 'custom',
        path: ['ukAccommodationType'],
        message: 'Accomodation type is required',
      });
    }
    if (data.termAddressSameAsPermanent === true && data.termAddress) {
      ctx.addIssue({
        code: 'custom',
        path: ['termAddress'],
        message: 'Term address should not be provided when it is the same as permanent address',
      });
    }

    if (data.hasUkAccommodation === false && data.ukAccommodationAddress) {
      ctx.addIssue({
        code: 'custom',
        path: ['ukAccommodationAddress'],
        message: 'UK accommodation address should not be provided when you do not have UK accommodation',
      });
    }

    if (data.hasUkAccommodation) {
      if (!data.ukAccommodationType) {
        ctx.addIssue({
          code: 'custom',
          path: ['ukAccommodationType'],
          message: 'Accomodation type is required',
        });
      }
      if (!data.ukAccommodationAddress) {
        ctx.addIssue({
          code: 'custom',
          path: ['ukAccommodationAddress'],
          message: 'Accomodation address is required',
        });
      }
      if (!data.ukAccommodationPhone) {
        ctx.addIssue({
          code: 'custom',
          path: ['ukAccommodationPhone'],
          message: 'Phone number is required',
        });
      }
    }

    if (data.contactAddress && data.contactAddress.country === 'United Kingdom') {
      if (!stringHasContent(data.contactAddress.state)) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactAddress', 'state'],
          message: 'A UK county is required for the contact address',
        });
      }
    }

    if (data.residentialAddress && data.residentialAddress.country === 'United Kingdom') {
      if (!stringHasContent(data.residentialAddress.state)) {
        ctx.addIssue({
          code: 'custom',
          path: ['residentialAddress', 'state'],
          message: 'A UK county is required for the residential address',
        });
      }
    }

    if (data.ukAccommodationAddress) {
      if (!stringHasContent(data.ukAccommodationAddress.state)) {
        ctx.addIssue({
          code: 'custom',
          path: ['ukAccommodationAddress', 'state'],
          message: 'A UK county is required for the UK accommodation address',
        });
      }
    }

    if (data.termAddress) {
      if (!stringHasContent(data.termAddress.state)) {
        ctx.addIssue({
          code: 'custom',
          path: ['termAddress', 'state'],
          message: 'A UK county is required for the term address',
        });
      }
    }
  });

export const enhanceContactDetailsSchema = (schema: typeof ContactDetailsSchema, feeStatus?: FeeStatus) => {
  return schema.superRefine((data, ctx) => {
    if (feeStatus === FeeStatus.Home) {
      if (data.termAddressSameAsPermanent === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['termAddressSameAsPermanent'],
          message: 'Required',
        });
      }
    } else if (feeStatus && [FeeStatus.NotSure, FeeStatus.Overseas].includes(feeStatus)) {
      if (data.hasUkAccommodation === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['hasUkAccommodation'],
          message: 'Required',
        });
      }
    }
  });
};

export const VisaNotListedOption = z.object({
  visaNotListedCustomValue: z.string().nullish(),
});

export const UkStudentVisaSchema = z
  .object({
    studentVisaType: z.string().refine(val => val in VisaType),
    levelOfStudy: z.string().refine(val => val in VisaLevelOfStudy),
    visaReceivedYear: z.number().gt(1900).lte(new Date().getFullYear()),
    isCourseCompleted: z.boolean(),
    compareCourse: z.string().refine(val => val in CourseCompare),
  })
  .merge(VisaNotListedOption);

export const UkNonStudentVisasSchema = z
  .object({
    studentVisaType: z.string().refine(val => val in VisaOrPermitType),
    issuedDate: z
      .string()
      .date('Date format is DD MM YYYY')
      .refine(
        val => {
          const [YYYY, MM, DD] = val.split('-');
          return (
            DD &&
            MM &&
            YYYY &&
            DD.length === 2 &&
            MM.length === 2 &&
            YYYY.length === 4 &&
            parseInt(DD) <= 31 &&
            parseInt(MM) <= 12 &&
            parseInt(YYYY) > 1900
          );
        },
        { message: 'Issue date is incomplete or invalid.' },
      )
      .refine(
        val => {
          const now = new Date();
          const issueDate = new Date(val);
          return issueDate < now && issueDate.toDateString() !== now.toDateString();
        },
        { message: 'Issue date cannot be in the future or today' },
      ),
    validTill: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val || val.trim() === '') return true; // Skip validation if empty
          const [YYYY, MM, DD] = val.split('-');
          return (
            DD &&
            MM &&
            YYYY &&
            DD.length === 2 &&
            MM.length === 2 &&
            YYYY.length === 4 &&
            parseInt(DD) <= 31 &&
            parseInt(MM) <= 12 &&
            parseInt(YYYY) > 1900
          );
        },
        { message: 'Valid until date is incomplete or invalid.' },
      )
      .refine(
        val => {
          if (!val || val.trim() === '') return true; // Skip validation if empty
          const date = new Date(val);
          // Check if the date is valid by ensuring it's not NaN
          // and the formatted parts match what was input
          const [inputYear, inputMonth, inputDay] = val.split('-').map(Number);
          return (
            !isNaN(date.getTime()) &&
            date.getFullYear() === inputYear &&
            date.getMonth() + 1 === inputMonth &&
            date.getDate() === inputDay
          );
        },
        { message: 'Please enter a valid date.' },
      ),
  })
  .merge(VisaNotListedOption)
  .superRefine((data, ctx) => {
    const issuedDate = new Date(data.issuedDate);
    const validTill = data.validTill ? new Date(data.validTill) : null;

    if (validTill && issuedDate > validTill) {
      ctx.addIssue({
        code: 'custom',
        path: ['issuedDate'],
        message: 'Issued date should be less than valid till date',
      });
    }

    if (VisaOrPermitType[data.studentVisaType as keyof typeof VisaOrPermitType] === VisaOrPermitType.VisaNotListed) {
      if (!data.visaNotListedCustomValue || data.visaNotListedCustomValue.trim().length < 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['visaNotListedCustomValue'],
          message: `Custom visa or permit type is required when student visa type is "${VisaOrPermitType.VisaNotListed}"`,
        });
      }
    }
  });

export const UkVisaRefusalSchema = z.object({
  refusedVisaType: z.string().refine(data => data in RefusedVisaType),
  visaRefusalYear: z.number({ invalid_type_error: 'Must be a number' }).min(1900).max(new Date().getFullYear()),
});

export const NonUkVisaRefusalSchema = z.object({
  visaRefusalCountry: z.string(),
  isStudentVisaRefused: z.boolean(),
  visaRefusalYear: z.number({ invalid_type_error: 'Must be a number' }).min(1900).max(new Date().getFullYear()),
});

function isBritishOrIrish(nationalityForApplicationPurposes: INationality['nationalityForApplicationPurposes']) {
  return nationalityForApplicationPurposes === 'British' || nationalityForApplicationPurposes === 'Irish';
}

function isEmptyOrNonExistentArray(arr: unknown): boolean {
  return !arr || !Array.isArray(arr) || arr.length === 0;
}

export const VisaHistorySchema = z
  .object({
    isVisaRequired: z.boolean().nullish(), //Overseas
    visaExemptionReason: z.string().nullish(), //Overseas
    hasVisaForCurrentOrPreviousUkStudy: z.boolean().nullish(), //Overseas
    hasStudentVisaForThisUkStudy: z.boolean().nullish(), //Overseas
    ukStudentVisas: z.array(UkStudentVisaSchema).nullish(), //Overseas
    hasEuEeaSwitzerlandExperience: z.boolean().nullish(), //ALL (HOME AND Overseas and British or Irish)
    hasEuFamilyMember: z.boolean().nullish(), //ALL (HOME AND Overseas and British or Irish)
    hasNonStudentVisaOrPermit: z.boolean().nullish(), // HOME (non British or Irish) AND Overseas
    ukNonStudentVisaOrPermit: z.array(UkNonStudentVisasSchema).nullish(), // HOME (non British or Irish) AND Overseas
    hasPreviousVisaRefusal: z.boolean().nullish(), //Overseas
    ukVisaRefusals: z.array(UkVisaRefusalSchema).nullish(), //Overseas
    hasPreviousNonUkVisaRefusal: z.boolean().nullish(), //Overseas
    nonUkVisaRefusals: z.array(NonUkVisaRefusalSchema).nullish(), //Overseas
  })
  .superRefine((data, ctx) => {
    const validationRules = [
      {
        condition: data.isVisaRequired === false && !data.visaExemptionReason,
        path: 'visaExemptionReason',
        message: 'Specify visa not required reason',
      },
      {
        condition: data.hasStudentVisaForThisUkStudy && isEmptyOrNonExistentArray(data.ukStudentVisas),
        path: 'ukStudentVisas',
        message: 'Specify your current or previous UK study visas',
      },
      {
        condition: data.hasNonStudentVisaOrPermit && isEmptyOrNonExistentArray(data.ukNonStudentVisaOrPermit),
        path: 'ukNonStudentVisaOrPermit',
        message: 'Specify your UK permit or non-student visas',
      },
      {
        condition: data.hasPreviousVisaRefusal && isEmptyOrNonExistentArray(data.ukVisaRefusals),
        path: 'ukVisaRefusals',
        message: 'Specify if visa is required',
      },
      {
        condition: data.hasPreviousNonUkVisaRefusal && isEmptyOrNonExistentArray(data.nonUkVisaRefusals),
        path: 'nonUkVisaRefusals',
        message: 'Specify your non-UK visa refusals',
      },
      {
        condition: data.hasEuEeaSwitzerlandExperience === undefined,
        path: 'hasEuEeaSwitzerlandExperience',
        message: 'Required',
      },
      {
        condition: data.hasEuFamilyMember === undefined,
        path: 'hasEuFamilyMember',
        message: 'Required',
      },
    ];

    // Apply all validation rules
    validationRules.forEach(rule => {
      if (rule.condition) {
        ctx.addIssue({
          code: 'custom',
          path: [rule.path],
          message: rule.message,
        });
      }
    });
  });

export function enhanceVisaHistorySchema(
  feeStatus: FeeStatus,
  nationalityForApplicationPurposes: INationality['nationalityForApplicationPurposes'],
) {
  return VisaHistorySchema.superRefine((data, ctx) => {
    if (!isBritishOrIrish(nationalityForApplicationPurposes)) {
      if (typeof data.hasNonStudentVisaOrPermit !== 'boolean') {
        ctx.addIssue({
          code: 'custom',
          path: ['hasNonStudentVisaOrPermit'],
          message: 'Specify your UK permit or non-student visas',
        });
      }
    }

    if (feeStatus === FeeStatus.Overseas) {
      const requiredFields = [
        'isVisaRequired',
        'hasVisaForCurrentOrPreviousUkStudy',
        'hasPreviousVisaRefusal',
        'hasPreviousNonUkVisaRefusal',
      ];
      requiredFields.forEach(field => {
        const fieldValue = data[field as keyof typeof data];
        if (fieldValue === null || fieldValue === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: 'This field is required',
          });
        }
      });
    }

    if (data.hasVisaForCurrentOrPreviousUkStudy && typeof data.hasStudentVisaForThisUkStudy !== 'boolean') {
      ctx.addIssue({
        code: 'custom',
        path: ['hasStudentVisaForThisUkStudy'],
        message: 'This field is required',
      });
    }
  });
}

export const GradingSystemSchema = z.object({
  id: z.number(),
  label: z.string(),
});

export const QualificationTypesSchema = z.object({
  id: z.number(),
  label: z.string(),
  awardingOrganisationsVisible: z.boolean(),
  modulesVisible: z.boolean(),
  subjectsVisible: z.boolean(),
  gradingSystems: z.array(GradingSystemSchema),
});

export const AwardingOrganisationSchema = z.object({
  id: z.number(),
  label: z.string(),
  acronym: z.string(),
});

export type HighestQualificationType = {
  code: string;
  codingLabel: string;
};

export interface ISITSData {
  externalReference: string;
}

export interface ApplicantDetails
  extends
    IContactInformation,
    IFeeStatus,
    IProgrammeChoices,
    IApplicantReferral,
    IApplicantIdentity,
    INationality,
    IDiversity,
    IApplicantEmployment,
    Partial<IEnglish>, // Is optional, home applicants for example won't need it
    IContactDetails,
    IVisaHistory {
  userId: string;
  email: string;
  knownBy?: string;
  regionId: string; // Will we need this here or on the application?
  isActive: boolean;
  routeStatus: Partial<Record<YourInformationTaskRoute, RouteStatus>> & Partial<Record<SectionRoute, RouteStatus>>;
  potentialDuplicate?: boolean;
  potentialDuplicateConfirmed?: boolean;
  duplicateMatches?: Array<PotentialMatchStudent>; // This will be properly typed when we have the schema
  agentId?: string;
  agentAccessStatus?: AgentApplicantAuthorisationStatus | null;
  highestQualification?: HighestQualificationType;
  ellucianData?: Partial<IEllucianData>;
  sitsData?: Partial<ISITSData>;

  studentNo?: string;
  studentNumberGeneratedByApply?: boolean;
  importedToCasShield?: boolean;
  partners?: string[] | null;

  initiatedByRole?: IUserRole;
}

export interface ILead {
  uni: string;
  userId: string;
  email: string;
  agentConsent?: boolean;
  welcomeStatus: boolean;
  referrer: string;
  headers: { [key: string]: string };
  studentAuthorisedAgent?: boolean;

  initiatedByRole?: IUserRole;
}

export const StudentDuplicateSchema = z.object({
  uni: z.string(),
  userId: z.string(),
  applicationId: z.string(),
  isDuplicate: z.boolean(),
  studentNo: z.string().optional(),
});

export const ApplicantDetailsSchema = z.object({
  givenName: z.string().min(1, 'Given name is required').max(100, 'Given name is too long'),
  middleName: z.string().optional(),
  familyName: z.string().min(1, 'Family name is required').max(100, 'Family name is too long'),
  email: z.string().email('Please enter a valid email address'),
  nationality: z.string().min(1, 'Nationality is required'),
  feeStatus: z.nativeEnum(FeeStatus),
  highestQualificationCode: z.string().optional(),
  agentId: z.string().min(1, 'Agent selection is required'),
  isVisaRequired: z.boolean().optional(),
  dob: z
    .string()
    .date()
    .refine(
      val => {
        const [YYYY, MM, DD] = val.split('-');
        return (
          DD &&
          MM &&
          YYYY &&
          DD.length === 2 &&
          MM.length === 2 &&
          YYYY.length === 4 &&
          parseInt(DD) <= 31 &&
          parseInt(MM) <= 12 &&
          parseInt(YYYY) > 1900
        );
      },
      { message: 'Date of birth is incomplete or invalid.' },
    )
    .refine(
      val => {
        const now = new Date();
        const dob = new Date(val);
        // not equal dateStrings allows to return FALSE if dob and now are the same date
        // allow only dates before now
        return dob < now && dob.toDateString() !== now.toDateString();
      },
      { message: 'Date of birth cannot be in the future or today' },
    )
    .refine(
      val => {
        const [year, month, day] = val.split('-').map(Number);
        const today = new Date();
        const todayMonth = today.getMonth() + 1; // getMonth() is zero based: Jan = 0
        const age =
          today.getFullYear() - year - (todayMonth < month || (todayMonth === month && today.getDate() < day) ? 1 : 0);
        return age > 13;
      },
      { message: 'Must be older than 13 years old.' },
    ),
  phoneNumber: z
    .string()
    .includes('-')
    .min(2)
    .regex(/^\d+(-\d+)*$/, 'Invalid phone number.'),
});

/* eslint-disable @typescript-eslint/no-empty-object-type */
export interface IAttachDocument extends z.infer<typeof AttachDocumentSchema> {}
export interface IFeeStatus extends z.infer<typeof FeeStatusSchema> {}
export interface IVisaRequired extends z.infer<typeof VisaRequiredSchema> {}
export interface IContactInformation extends z.infer<typeof ContactInformationSchema> {}
export interface IProgrammeChoice extends z.infer<typeof ProgrammeChoiceSchema> {}
export interface IProgrammeChoices extends z.infer<typeof ProgrammeChoicesSchema> {}
export interface IProgrammeChoicesWithMax extends z.infer<typeof ProgrammeChoicesSchemaWithMax> {}
export interface IQualification extends z.infer<typeof QualificationSchema> {}
export interface IQualificationModule extends z.infer<typeof ModuleSchema> {}
export interface IStudy extends z.infer<typeof StudySchema> {}
export interface IStudyHistory extends z.infer<typeof StudyHistorySchema> {}
export interface IApplicantReferral extends z.infer<typeof ApplicantReferralSchema> {}
export interface IDiversity extends z.infer<typeof DiversitySchema> {}

export interface IApplicantIdentity extends z.infer<typeof ApplicantIdentitySchema> {}
export interface IApplicantIdentityValidation extends z.infer<typeof ApplicantIdentityValidationSchema> {}

export interface IOtherNames extends z.infer<typeof OtherNamesSchema> {}
export interface IOtherNamePayload extends z.infer<typeof OtherNamePayloadSchema> {}

export interface IOtherName extends z.infer<typeof OtherNameSchema> {}
export interface INationality extends z.infer<typeof NationalitySchema> {}
export interface IOtherNationality extends z.infer<typeof OtherNationalitySchema> {}
export interface IOtherResidence extends z.infer<typeof OtherResidenceEntrySchema> {}
export interface IEmploymentHistory extends z.infer<typeof EmploymentHistorySchema> {}
export interface IApplicantEmployment extends z.infer<typeof ApplicantEmploymentSchema> {}
export interface IEnglish extends z.infer<typeof EnglishSchema> {}
export type IEnglishTest = EnglishTestUnion;
export interface IContactDetails extends z.infer<typeof ContactDetailsSchema> {}
export interface IVisaHistory extends z.infer<typeof VisaHistorySchema> {}

export interface INonUkVisaRefusalSchema extends z.infer<typeof NonUkVisaRefusalSchema> {}
export interface IUkNonStudentVisaSchema extends z.infer<typeof UkNonStudentVisasSchema> {}
export interface IUkStudentVisaSchema extends z.infer<typeof UkStudentVisaSchema> {}
export interface IUkVisaRefusalSchema extends z.infer<typeof UkVisaRefusalSchema> {}

export interface IAddressSchema extends z.infer<typeof AddressSchema> {}

export interface IVisaNotListedOption extends z.infer<typeof VisaNotListedOption> {}

export interface IStudentDuplicate extends z.infer<typeof StudentDuplicateSchema> {}
export interface IApplicantData extends z.infer<typeof ApplicantDetailsSchema> {}

export interface IQualificationTypes extends z.infer<typeof QualificationTypesSchema> {}
export interface IAwardingOrganisation extends z.infer<typeof AwardingOrganisationSchema> {}
